/**
 * Going in and coming out of Sid's back room (docs/WEAPONS.md §6), as a small pure state machine the
 * back-room system steps every rendered frame:
 *
 *   street ─enter()→ darkenIn ─(black)→ revealIn → inside ─leave()→ darkenOut ─(black)→ revealOut → street
 *
 * The swap happens while the screen is black: `update` returns 'swapIn' (put the room up, the city
 * down) or 'swapOut' (the other way) on that frame, and 'out' on the frame the visit is over. A leave
 * asked for while the door is still opening turns round at once from however dark it already is.
 */

export type VisitPhase = 'street' | 'darkenIn' | 'revealIn' | 'inside' | 'darkenOut' | 'revealOut';
export type VisitEvent = 'swapIn' | 'swapOut' | 'out' | null;

/** Seconds for each half of the fade (to black, from black). */
export const FADE_HALF = 0.32;

export class BackRoomVisit {
  private phase_: VisitPhase = 'street';
  /** 0..1 progress through the current fade half. */
  private t = 0;

  get phase(): VisitPhase {
    return this.phase_;
  }

  /** The room is (or is about to be) on screen: the counter takes input. */
  get inside(): boolean {
    return this.phase_ === 'revealIn' || this.phase_ === 'inside';
  }

  /** Anything but the street: the player is busy at the door. */
  get busy(): boolean {
    return this.phase_ !== 'street';
  }

  /** 0 = clear, 1 = black: the fade layer's opacity. */
  get black(): number {
    switch (this.phase_) {
      case 'darkenIn':
      case 'darkenOut':
        return this.t;
      case 'revealIn':
      case 'revealOut':
        return 1 - this.t;
      default:
        return 0;
    }
  }

  /** Step through the door. False if already in (or on the way). */
  enter(): boolean {
    if (this.phase_ !== 'street') return false;
    this.phase_ = 'darkenIn';
    this.t = 0;
    return true;
  }

  /** Back out to the yard. False if already out (or on the way). */
  leave(): boolean {
    if (this.phase_ === 'inside' || this.phase_ === 'revealIn') {
      // From the room (or while it is still fading up): fade down from wherever the screen is now.
      const dark = this.black;
      this.phase_ = 'darkenOut';
      this.t = dark;
      return true;
    }
    if (this.phase_ === 'darkenIn') {
      // Never got in: the room was never put up, so just lift the black again.
      this.phase_ = 'revealOut';
      this.t = 1 - this.t;
      return true;
    }
    return false;
  }

  /** Straight back to the street with no fade (the UI took the screen, the law turned up). */
  reset(): void {
    this.phase_ = 'street';
    this.t = 0;
  }

  update(dt: number): VisitEvent {
    if (this.phase_ === 'street' || this.phase_ === 'inside') return null;
    this.t = Math.min(1, this.t + Math.max(0, dt) / FADE_HALF);
    if (this.t < 1) return null;
    this.t = 0;
    switch (this.phase_) {
      case 'darkenIn':
        this.phase_ = 'revealIn';
        return 'swapIn';
      case 'revealIn':
        this.phase_ = 'inside';
        return null;
      case 'darkenOut':
        this.phase_ = 'revealOut';
        return 'swapOut';
      case 'revealOut':
        this.phase_ = 'street';
        return 'out';
    }
    return null;
  }
}
